import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  RotateCcw,
  Save,
} from "lucide-react";
import { useId, useState, type ReactNode } from "react";

import { QueryPendingState } from "@/api/QueryPendingState";
import { queryKeys } from "@/api/hooks";
import { isTerminalQueryError } from "@/api/queryClient";
import {
  classifySummaryPresentationReadFailure,
  classifySummaryPresentationWriteFailure,
  useReplaceSummaryPresentationMutation,
  useSummaryPresentationQuery,
  type ProjectSummaryPresentation,
  type ReplaceProjectSummaryPresentationRequest,
  type SummaryPresentationWriteFailure,
} from "@/api/summaryPresentation";
import type { SummaryStructureRegistry } from "@/api/summaryStructure";
import { useCapability } from "@/auth/SessionContext";
import { cn } from "@/lib/cn";
import { formatDateTime } from "@/lib/format";
import { SUMMARY_OWNERSHIP_LABEL } from "@/lib/labels";
import { sectionsInDisplayOrder } from "@/lib/summaryStructure";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Select } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import {
  DeniedState,
  ErrorState,
  StaleDataNotice,
  UnavailableState,
} from "@/ui/states";

import { Identity } from "./SummaryIdentity";

type PresentationDraft = Pick<
  ReplaceProjectSummaryPresentationRequest,
  "typography" | "sections"
>;

/**
 * Project-scoped renderer controls. Their state and compare-and-set identity
 * come from the dedicated presentation endpoint; the request schema contains
 * no prompt pin or runtime clinical value, so this surface cannot write either
 * adjacent Summary layer.
 */
export function SummaryPresentationControls({
  registry,
  projectId,
}: {
  registry: SummaryStructureRegistry;
  projectId: string;
}) {
  const stateQuery = useSummaryPresentationQuery(projectId);
  const terminalFailure =
    stateQuery.isError && isTerminalQueryError(stateQuery.error);
  const state = terminalFailure ? undefined : stateQuery.data;

  return (
    <section className="space-y-3" aria-labelledby="summary-presentation">
      <div className="space-y-1">
        <h3
          id="summary-presentation"
          className="text-sm font-semibold text-foreground"
        >
          Presentation and rendering
        </h3>
        <p className="text-sm text-muted-foreground">
          Typography, report inclusion and display order are{" "}
          {SUMMARY_OWNERSHIP_LABEL.target_renderer_owned.toLowerCase()}. They
          change how this Project&apos;s Summary is rendered, never its prompt
          pins or runtime clinical record.
        </p>
      </div>

      {stateQuery.isPending ? (
        <QueryPendingState
          isPaused={stateQuery.isPaused}
          failureReason={stateQuery.failureReason}
          label="Loading this Project's Summary presentation"
          errorTitle="The Summary presentation could not be loaded"
          rows={4}
          queryKey={queryKeys.projectSummaryPresentation(projectId)}
        />
      ) : state === undefined ? (
        <PresentationReadFailure
          error={stateQuery.error}
          onRetry={() => void stateQuery.refetch()}
        />
      ) : (
        <>
          {stateQuery.isError ? (
            <StaleDataNotice
              updatedAt={stateQuery.dataUpdatedAt}
              onRetry={() => void stateQuery.refetch()}
            />
          ) : null}
          <PresentationEditor
            registry={registry}
            projectId={projectId}
            state={state}
            refetch={stateQuery.refetch}
          />
        </>
      )}
    </section>
  );
}

function PresentationEditor({
  registry,
  projectId,
  state,
  refetch,
}: {
  registry: SummaryStructureRegistry;
  projectId: string;
  state: ProjectSummaryPresentation;
  refetch: ReturnType<typeof useSummaryPresentationQuery>["refetch"];
}) {
  const write = useCapability("draft.write");
  const mutation = useReplaceSummaryPresentationMutation(projectId);
  const permissionId = useId();
  const [baseline, setBaseline] = useState(state);
  const [draft, setDraft] = useState<PresentationDraft>(() => draftFrom(state));
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const dirty = !sameDraft(draft, draftFrom(baseline));
  const remoteMoved =
    state.presentationVersion !== baseline.presentationVersion;
  const failure = mutation.isError
    ? classifySummaryPresentationWriteFailure(mutation.error)
    : null;

  const unresolvedFailure =
    failure !== null &&
    (failure.kind === "stale" || failure.kind === "ambiguous");
  const editingBlocked =
    !write.allowed || mutation.isPending || remoteMoved || unresolvedFailure;
  const blockingReason = !write.allowed
    ? write.reason
    : mutation.isPending
      ? "Saving the complete presentation document."
      : remoteMoved || unresolvedFailure
        ? "The server state must be reloaded before this draft can be edited or saved."
        : null;

  function changeDraft(
    update: (current: PresentationDraft) => PresentationDraft,
  ) {
    if (editingBlocked) return;
    setDraft(update);
    mutation.reset();
    setStatusMessage(null);
  }

  function move(slug: string, direction: -1 | 1) {
    changeDraft((current) => {
      const ordered = [...current.sections].sort((a, b) => a.order - b.order);
      const from = ordered.findIndex((section) => section.slug === slug);
      const to = from + direction;
      if (from < 0 || to < 0 || to >= ordered.length) return current;
      const neighbour = ordered[to];
      const selected = ordered[from];
      if (neighbour === undefined || selected === undefined) return current;
      return {
        ...current,
        sections: current.sections.map((section) =>
          section.slug === selected.slug
            ? { ...section, order: neighbour.order }
            : section.slug === neighbour.slug
              ? { ...section, order: selected.order }
              : section,
        ),
      };
    });
  }

  async function save() {
    if (editingBlocked || !dirty) return;
    setStatusMessage(null);
    try {
      const saved = await mutation.mutateAsync({
        expectedRegistryVersion: registry.version,
        expectedPresentationVersion: baseline.presentationVersion,
        typography: { ...draft.typography },
        sections: draft.sections.map((section) => ({ ...section })),
      });
      setBaseline(saved);
      setDraft(draftFrom(saved));
      setStatusMessage(
        `Presentation saved as Project version ${saved.presentationVersion}.`,
      );
    } catch {
      // The mutation state renders the exact recovery. A stale or ambiguous
      // write is never resent automatically.
    }
  }

  async function reloadLatest() {
    const result = await refetch();
    if (result.data !== undefined && !result.isError) {
      setBaseline(result.data);
      setDraft(draftFrom(result.data));
      mutation.reset();
      setStatusMessage(
        `Loaded Project presentation version ${result.data.presentationVersion}; the previous local draft was discarded.`,
      );
    }
  }

  const ordered = [...draft.sections].sort((a, b) => a.order - b.order);
  const typography = registry.typography;

  return (
    <div className="space-y-4">
      <PresentationIdentity state={baseline} />

      <p id={permissionId} className="text-sm text-muted-foreground">
        {blockingReason ??
          "Changes remain local until Save presentation. The whole renderer document commits atomically under the Project version shown above."}
      </p>

      {remoteMoved && failure?.kind !== "stale" ? (
        <Notice
          role="alert"
          tone="destructive"
          icon={<AlertTriangle className="h-4 w-4" />}
          action={
            <Button variant="outline" onClick={() => void reloadLatest()}>
              <RefreshCw aria-hidden="true" className="h-4 w-4" />
              Reload latest
            </Button>
          }
        >
          Another save produced Project presentation version{" "}
          {state.presentationVersion}. Your unsaved draft is still here and has
          not been submitted over it.
        </Notice>
      ) : null}

      {failure ? (
        <PresentationWriteNotice
          failure={failure}
          onReload={() => void reloadLatest()}
        />
      ) : null}

      <div className="space-y-4 rounded-md border border-border p-4">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="min-w-0 flex-1 text-sm font-medium text-foreground">
            Typography
          </span>
          <Badge tone="outline">
            {SUMMARY_OWNERSHIP_LABEL[typography.ownership]}
          </Badge>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <DeclaredField
            label="Base font family"
            hint={`${typography.fontFamilies.length} server-declared families.`}
            reasonId={permissionId}
          >
            {({ controlId, describedBy }) => (
              <Select
                id={controlId}
                value={draft.typography.fontFamily}
                aria-disabled={editingBlocked || undefined}
                aria-describedby={describedBy}
                onChange={(event) =>
                  changeDraft((current) => ({
                    ...current,
                    typography: {
                      ...current.typography,
                      fontFamily: event.target.value,
                    },
                  }))
                }
              >
                {typography.fontFamilies.map((family) => (
                  <option key={family} value={family}>
                    {family}
                  </option>
                ))}
              </Select>
            )}
          </DeclaredField>

          <DeclaredField
            label="Base font size"
            hint={`Server-declared range ${typography.size.min}–${typography.size.max}.`}
            reasonId={permissionId}
          >
            {({ controlId, describedBy }) => (
              <Select
                id={controlId}
                value={draft.typography.baseFontSize}
                aria-disabled={editingBlocked || undefined}
                aria-describedby={describedBy}
                onChange={(event) =>
                  changeDraft((current) => ({
                    ...current,
                    typography: {
                      ...current.typography,
                      baseFontSize: Number(event.target.value),
                    },
                  }))
                }
              >
                {integerRange(typography.size.min, typography.size.max).map(
                  (size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ),
                )}
              </Select>
            )}
          </DeclaredField>
        </div>
      </div>

      <ul className="space-y-2">
        {ordered.map((section, index) => (
          <li
            key={section.slug}
            className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-border px-4 py-3"
          >
            <Identity className="min-w-0 flex-1 basis-full text-sm sm:basis-auto">
              {section.slug}
            </Identity>

            <span className="flex items-center gap-2 text-xs text-muted-foreground">
              <button
                type="button"
                role="checkbox"
                aria-checked={section.visible}
                aria-disabled={editingBlocked || undefined}
                aria-describedby={permissionId}
                aria-label={`${section.slug} included in the report`}
                onClick={() =>
                  changeDraft((current) => ({
                    ...current,
                    sections: current.sections.map((item) =>
                      item.slug === section.slug
                        ? { ...item, visible: !item.visible }
                        : item,
                    ),
                  }))
                }
                className={cn(
                  "flex h-5 w-5 items-center justify-center rounded border border-input",
                  "aria-checked:border-primary aria-checked:bg-primary aria-checked:text-primary-foreground",
                  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
                  "aria-disabled:cursor-not-allowed aria-disabled:opacity-50",
                )}
              >
                {section.visible ? (
                  <Check aria-hidden="true" className="h-3 w-3" />
                ) : null}
              </button>
              Include
            </span>

            <Identity>{section.order}</Identity>
            <Button
              variant="outline"
              size="icon"
              disabled={editingBlocked || index === 0}
              aria-describedby={permissionId}
              aria-label={`Move ${section.slug} earlier in the report`}
              onClick={() => move(section.slug, -1)}
            >
              <ChevronUp aria-hidden="true" className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              disabled={editingBlocked || index === ordered.length - 1}
              aria-describedby={permissionId}
              aria-label={`Move ${section.slug} later in the report`}
              onClick={() => move(section.slug, 1)}
            >
              <ChevronDown aria-hidden="true" className="h-4 w-4" />
            </Button>
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button
          variant="outline"
          disabled={editingBlocked}
          aria-describedby={permissionId}
          onClick={() =>
            changeDraft(() => ({
              typography: {
                fontFamily: typography.defaultFontFamily,
                baseFontSize: typography.size.default,
              },
              sections: sectionsInDisplayOrder(registry).map((section) => ({
                slug: section.slug,
                visible: section.presentation.visible,
                order: section.presentation.order,
              })),
            }))
          }
        >
          <RotateCcw aria-hidden="true" className="h-4 w-4" />
          Restore registry defaults
        </Button>
        <Button
          disabled={editingBlocked || !dirty}
          aria-describedby={permissionId}
          onClick={() => void save()}
        >
          <Save aria-hidden="true" className="h-4 w-4" />
          {mutation.isPending ? "Saving…" : "Save presentation"}
        </Button>
      </div>

      <p role="status" aria-live="polite" className="sr-only">
        {statusMessage}
      </p>
    </div>
  );
}

function PresentationIdentity({
  state,
}: {
  state: ProjectSummaryPresentation;
}) {
  return (
    <dl className="flex flex-wrap gap-x-6 gap-y-2 rounded-md border border-border bg-secondary/40 px-4 py-3 text-xs">
      <Fact label="Project presentation">
        <Identity>v{state.presentationVersion}</Identity>
      </Fact>
      <Fact label="Source">
        {state.source === "registry_defaults"
          ? "Registry defaults"
          : "Project override"}
      </Fact>
      <Fact label="Registry">
        <Identity>{state.registryVersion}</Identity>
      </Fact>
      <Fact label="Registry digest">
        <Identity title={state.registryDigest}>
          {state.registryDigest.slice(0, 12)}…
        </Identity>
      </Fact>
      <Fact label="Last saved">{formatDateTime(state.updatedAt)}</Fact>
    </dl>
  );
}

function PresentationReadFailure({
  error,
  onRetry,
}: {
  error: unknown;
  onRetry: () => void;
}) {
  const failure = classifySummaryPresentationReadFailure(error);
  if (failure.kind === "denied") {
    return (
      <DeniedState
        title="This Project's Summary presentation could not be loaded"
        description="Your session cannot read this Project's renderer state."
      />
    );
  }
  if (failure.kind === "unavailable") {
    return (
      <UnavailableState
        title="Project presentation is not available in this deployment"
        description="The Editor Service has not published the governed Project presentation endpoint. There is nothing to retry."
      />
    );
  }
  if (failure.kind === "structure_mismatch") {
    return (
      <ErrorState
        title="This build cannot render the served Summary structure"
        description={`This build renders the registry version shown above; the service reported ${failure.currentRegistryVersion ?? "another version"}. Reload after the matching application build is deployed.`}
      />
    );
  }
  if (failure.kind === "stored_registry_mismatch") {
    return (
      <ErrorState
        title="This Project's presentation belongs to another Summary structure"
        description={`Stored registry ${failure.storedRegistryVersion ?? "not reported"}; current registry ${failure.currentRegistryVersion ?? "not reported"}. The service refused to reinterpret the saved renderer state.`}
      />
    );
  }
  return (
    <ErrorState
      title="The Summary presentation could not be loaded"
      description={
        failure.kind === "upstream"
          ? "The Editor Service could not read the renderer state."
          : `Error ${failure.code}${failure.requestId ? ` · Request ${failure.requestId}` : ""}`
      }
      onRetry={onRetry}
    />
  );
}

function PresentationWriteNotice({
  failure,
  onReload,
}: {
  failure: SummaryPresentationWriteFailure;
  onReload: () => void;
}) {
  if (failure.kind === "stale") {
    return (
      <Notice
        role="alert"
        tone="destructive"
        icon={<AlertTriangle className="h-4 w-4" />}
        action={
          <Button variant="outline" onClick={onReload}>
            <RefreshCw aria-hidden="true" className="h-4 w-4" />
            Reload latest
          </Button>
        }
      >
        Another editor saved Project presentation version{" "}
        {failure.currentPresentationVersion ?? "not reported"}. Your draft was
        not applied and remains visible until you explicitly reload it.
      </Notice>
    );
  }
  if (failure.kind === "ambiguous") {
    return (
      <Notice
        role="alert"
        tone="destructive"
        icon={<AlertTriangle className="h-4 w-4" />}
        action={
          <Button variant="outline" onClick={onReload}>
            <RefreshCw aria-hidden="true" className="h-4 w-4" />
            Read server state
          </Button>
        }
      >
        The save result is unknown. It was not retried, because the server may
        already have committed it. Read the server state before editing again.
      </Notice>
    );
  }
  return (
    <Notice role="alert" tone="destructive">
      {writeFailureMessage(failure)}
    </Notice>
  );
}

function writeFailureMessage(failure: SummaryPresentationWriteFailure): string {
  switch (failure.kind) {
    case "denied":
      return "The Editor Service refused permission to save this Project's presentation.";
    case "unavailable":
      return "This deployment does not provide the Project presentation write surface.";
    case "structure_mismatch":
      return `The served Summary registry changed to ${failure.currentRegistryVersion ?? "another version"}; this draft was not written.`;
    case "stored_registry_mismatch":
      return `The stored presentation belongs to registry ${failure.storedRegistryVersion ?? "not reported"}, not ${failure.currentRegistryVersion ?? "the current registry"}; this draft was not written.`;
    case "upstream":
      return "The Editor Service could not save this presentation.";
    case "invalid":
    case "error":
      return `The presentation was not saved (${failure.code}${failure.requestId ? ` · Request ${failure.requestId}` : ""}).`;
    case "stale":
    case "ambiguous":
      return "The presentation was not saved.";
  }
}

function draftFrom(state: ProjectSummaryPresentation): PresentationDraft {
  return {
    typography: { ...state.typography },
    sections: state.sections.map((section) => ({ ...section })),
  };
}

function sameDraft(left: PresentationDraft, right: PresentationDraft): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function integerRange(min: number, max: number): number[] {
  return Array.from({ length: max - min + 1 }, (_, index) => min + index);
}

function DeclaredField({
  label,
  hint,
  reasonId,
  children,
}: {
  label: string;
  hint: string;
  reasonId: string;
  children: (ids: { controlId: string; describedBy: string }) => ReactNode;
}) {
  const controlId = useId();
  const hintId = useId();
  return (
    <div className="space-y-1.5">
      <label
        htmlFor={controlId}
        className="text-sm font-medium text-foreground"
      >
        {label}
      </label>
      {children({ controlId, describedBy: `${reasonId} ${hintId}` })}
      <p id={hintId} className="text-xs text-muted-foreground">
        {hint}
      </p>
    </div>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="font-medium text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-foreground">{children}</dd>
    </div>
  );
}
